/**
 * The dock's offline queue: an incoming inspection (IIR) the tablet could not
 * send yet, held in IndexedDB until it can (review amendment B2, 22 Oct 2026).
 *
 * ⚠ THE ATTRIBUTION RULES ARE queue.ts's, NOT NEW ONES. Read that file's header
 * first. In short, and both halves are needed:
 *
 *   1. The UPN is stamped at ENQUEUE, from the session, applied last so nothing
 *      a call site passes can set it. The server takes the inspector from the
 *      token (rule 2), so the stamp is what keeps a queue that outlives a
 *      handover from sending one person's inspection under another's name.
 *   2. Replay sends only rows whose stamp is the account signed in now. Another
 *      account's row is HELD — never re-attributed, never silently dropped. An
 *      unstamped row matches nobody. Nobody signed in holds quietly.
 *
 * WHERE IT DIFFERS FROM THE READINGS QUEUE, and why:
 *
 *   · A 4xx other than 401 and a session 403 is NOT deleted (a 403 no_current_grant
 *     counts: it is the gate's refusal, not the session's). A rejected reading is stored by
 *     the server (rule 4) so the device can forget it; a refused inspection is
 *     NOT stored — the server only refuses a malformed one — so deleting it here
 *     would lose the only copy. It stays on the device, marked refused with the
 *     server's sentence, is not retried, and leaves only by its owner's explicit
 *     discard.
 *   · A separate IndexedDB database, so neither store's version can break the other.
 *   · The tablet mints `clientInspectionId` here, once, so a retry of an
 *     inspection the server already has comes back as that inspection (200,
 *     duplicate) rather than a second one.
 *
 * PHOTOS (migration 019; Amit, 22 Oct 2026: ALWAYS OPTIONAL). They are held in a
 * store of their own beside the inspection, stamped with the same UPN at the same
 * enqueue, and sent only AFTER the server has accepted the inspection (they need
 * its id), one request each. ⚠ A photo never blocks or loses the inspection: the
 * inspection is sent first and deleted on acceptance whatever the photos do; a
 * photo the network drops, or the server cannot take just now (5xx), waits and
 * goes on the next replay with the inspection queue's backoff; only a photo the
 * server REFUSES (a 4xx) is marked "not sent", for its owner to send again or
 * discard (review, 22 Oct 2026). Another account's photos are held exactly like its
 * inspections. Hand over counts unsent photos, because only their owner can send
 * them (the server refuses a photo on somebody else's inspection).
 *
 * Dependencies are injected so every rule has a test with no browser.
 */
import { apiUrl } from '../config/api';
import { normaliseUpn, rowUpn, mineToSend } from './queue';

export interface IirContext {
  vendorName: string;
  /** "PO025954 / 1 Lentil chips, PO025955 / 2 …" — enough to name a held item offline. */
  label: string;
}

export interface IirRow {
  id: number;
  /** The account that captured it, normalised. `''` matches nobody. */
  upn: string;
  payload: Record<string, unknown> & { clientInspectionId: string; capturedAtDevice: string };
  context: IirContext;
  enqueuedAt: string;
  /** The server refused it (a 4xx other than auth): kept, not retried. */
  refused: { status: number; reason: string | null; message: string } | null;
}

/** A photo waiting to go with (after) its inspection. */
export interface IirPhotoRow {
  id: number;
  /** Stamped at enqueue, with the inspection's own stamp. */
  upn: string;
  clientInspectionId: string;
  /** The server's id for the inspection, once it accepted it. Null = the inspection is not accepted yet. */
  inspectionId: number | null;
  /** The row's sequence on the inspection, or null for the delivery. */
  batchSequence: number | null;
  checkCode: string | null;
  blob: Blob;
  capturedAtDevice: string;
  context: IirContext;
  /** The server refused it, or could not keep it: kept, not retried until its owner says so. */
  failed: { status: number; reason: string | null; message: string } | null;
}

export interface PhotoInput {
  blob: Blob;
  capturedAtDevice: string;
  batchSequence: number | null;
  checkCode: string | null;
}

export interface IirStore {
  add(row: Omit<IirRow, 'id'>): Promise<number>;
  all(): Promise<IirRow[]>;
  put(row: IirRow): Promise<void>;
  delete(id: number): Promise<void>;
  addPhoto(row: Omit<IirPhotoRow, 'id'>): Promise<number>;
  allPhotos(): Promise<IirPhotoRow[]>;
  putPhoto(row: IirPhotoRow): Promise<void>;
  deletePhoto(id: number): Promise<void>;
}

/** What the server said about an inspection it took. */
export interface IirResult {
  clientInspectionId: string;
  inspectionId: number | null;
  result: 'PASS' | 'FAIL' | null;
  /** On a FAIL: the sentence that it is now an inspection failure for QA, in the app. */
  failureLine: string | null;
  context: IirContext;
  at: number;
}

export interface IirSnapshot {
  /** This account's rows still to send, or null when the store could not be read
   *  (unknown is not zero - queue.ts's QueueSnapshot.queued). */
  pending: number | null;
  /** Other accounts' rows, waiting for their owner to sign in here. */
  held: Array<{ queueId: number; upn: string; context: IirContext; capturedAtDevice: string }>;
  /** This account's rows the server refused: on the device, not retried. */
  refused: Array<{ queueId: number; context: IirContext; message: string; reason: string | null }>;
  /** What the server said about the ones it took, newest first, until acknowledged. */
  results: IirResult[];
  /** This account's photos still to send (their inspection accepted or not), or null when unknown. */
  photosPending: number | null;
  /** This account's photos the server did not take: "photo not sent", with Send again / Discard. */
  photosFailed: Array<{ photoId: number; context: IirContext; message: string; reason: string | null }>;
  /** Other accounts' photos, waiting for their owner. */
  photosHeld: number;
}

export type IirReplayStop = 'drained' | 'nobody_signed_in' | 'nothing_for_this_account' | 'network' | 'server_error' | 'auth' | 'already_running' | 'store_unavailable';

export interface IirQueueDeps {
  idb: IirStore;
  getUpn: () => string;
  getToken: () => Promise<string | null>;
  fetch: typeof fetch;
  now: () => number;
  newId: () => string;
  endpoint: string;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

export interface IirQueue {
  enqueue(payload: Record<string, unknown>, context: IirContext, photos?: readonly PhotoInput[]): Promise<IirRow>;
  replay(): Promise<IirSnapshot & { sent: number; stoppedBecause: IirReplayStop }>;
  snapshot(): Promise<IirSnapshot>;
  /** Only the row's own account may discard it, naming itself. */
  discard(queueId: number, ownerUpn: string): Promise<void>;
  /** A refused row, sent again by its owner (e.g. once their RECEIVE grant exists). */
  retry(queueId: number, ownerUpn: string): Promise<void>;
  /** A photo not sent, sent again by its owner. */
  retryPhoto(photoId: number, ownerUpn: string): Promise<void>;
  /** A photo not sent, dropped by its owner. The inspection is untouched. */
  discardPhoto(photoId: number, ownerUpn: string): Promise<void>;
  acknowledgeResults(): void;
  subscribe(listener: (s: IirSnapshot) => void): () => void;
  scheduleReplay(): void;
}

const DB_NAME = 'quality_iir_queue';
const STORE = 'inspections';
/** Version 2 (migration 019): the photos, beside the inspections. */
const PHOTOS = 'photos';
/** Version 3 (UI/UX audit, 31 Oct 2026, UX-08): the half-done inspection, beside the queue. */
const DRAFTS = 'drafts';
const DB_VERSION = 3;
const BACKOFF_BASE_MS = 2_000;
const BACKOFF_CAP_MS = 60_000;
const MAX_RESULTS = 10;

/**
 * One connection to the dock's database, and the one way to issue a request in it.
 *
 * Shared by the queue's store and the draft store (UI/UX audit, 31 Oct 2026, UX-08) so both open
 * the SAME version with the SAME upgrade: whichever opens first creates every store, and neither can
 * leave the other on a database without its store.
 */
function iirDatabase() {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const open = () =>
    (dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      // ⚠ ADDITIVE ONLY. Each store is created when missing and nothing is ever deleted or
      // re-created, so a tablet upgrading from v1 or v2 keeps every inspection and photo it holds:
      // those rows are the only copy (the server has not got them).
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        if (!req.result.objectStoreNames.contains(PHOTOS)) req.result.createObjectStore(PHOTOS, { keyPath: 'id', autoIncrement: true });
        // v3: the half-done inspection, one per account, keyed by its owner's UPN.
        if (!req.result.objectStoreNames.contains(DRAFTS)) req.result.createObjectStore(DRAFTS, { keyPath: 'upn' });
      };
      req.onsuccess = () => {
        // A newer version opened in another tab: let go, so its upgrade is not blocked by this one.
        req.result.onversionchange = () => {
          req.result.close();
          dbPromise = null;
        };
        resolve(req.result);
      };
      // Another tab still holds the old version open (audit, 28 Oct 2026, as queue.ts): say so and
      // forget the attempt so the next call tries again - never hang an inspection silently.
      req.onblocked = () => {
        dbPromise = null;
        reject(new Error('Quality is open in another tab on this device. Close it, then send again.'));
      };
      req.onerror = () => {
        dbPromise = null;
        reject(req.error);
      };
    }));
  // The request is issued in the same synchronous step the transaction opens -
  // see queue.ts's `run` for why an await in between loses writes.
  const run = async <T>(mode: IDBTransactionMode, issue: (s: IDBObjectStore) => IDBRequest<T>, store = STORE): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const r = issue(db.transaction(store, mode).objectStore(store));
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  };
  return run;
}

export function indexedDbIirStore(): IirStore {
  const run = iirDatabase();
  return {
    add: async (row) => Number(await run('readwrite', (s) => s.add(row) as IDBRequest<IDBValidKey>)),
    all: async () => ((await run('readonly', (s) => s.getAll() as IDBRequest<IirRow[]>)) ?? []).slice().sort((a, b) => a.id - b.id),
    put: async (row) => void (await run('readwrite', (s) => s.put(row))),
    delete: async (id) => void (await run('readwrite', (s) => s.delete(id))),
    addPhoto: async (row) => Number(await run('readwrite', (s) => s.add(row) as IDBRequest<IDBValidKey>, PHOTOS)),
    allPhotos: async () =>
      ((await run('readonly', (s) => s.getAll() as IDBRequest<IirPhotoRow[]>, PHOTOS)) ?? []).slice().sort((a, b) => a.id - b.id),
    putPhoto: async (row) => void (await run('readwrite', (s) => s.put(row), PHOTOS)),
    deletePhoto: async (id) => void (await run('readwrite', (s) => s.delete(id), PHOTOS)),
  };
}

/* ── The half-done inspection (UI/UX audit, 31 Oct 2026, UX-08) ─────────────
 *
 * WHY. The dock form lived only in React state, and only Cancel asked "Leave this inspection?".
 * An area tab, a reload, a tab the browser discarded, or a sign-in redirect dropped it without a
 * word - seven vehicle checks and a row of photos, re-done with the driver waiting. So the draft
 * is kept here, photos included (already scaled JPEG blobs, which IndexedDB stores as they are),
 * and offered back on return to /op/receive.
 *
 * ⚠ RULE 2'S SHAPE, AS THE QUEUE'S. A draft is stamped with the account signed in when it was
 * kept, and it is only ever read back BY that account: it is keyed by the owner's UPN, `load`
 * answers nothing for anyone else (and for nobody signed in), and `clear` can only name the
 * caller's own key. Another person's draft is never shown, never resumed under a second name, and
 * never discarded by anyone but its owner - it is simply left until they come back. An attribution
 * the server takes from the token (rule 2) would otherwise turn person A's half-done checks into
 * person B's inspection the moment B pressed Hand over.
 *
 * One draft per account per device: a person inspects one truck at a time, and a second draft
 * would only raise the question of which one is real.
 */
export interface IirDraftRow<D = unknown> {
  /** The owner, normalised. Also the key. */
  upn: string;
  draft: D;
  /** "EXAMPLE SNACKS": enough to offer it back ("Resume the inspection of …"). */
  label: string;
  savedAt: string;
}

export interface IirDraftStore {
  get(upn: string): Promise<IirDraftRow | undefined>;
  put(row: IirDraftRow): Promise<void>;
  delete(upn: string): Promise<void>;
}

export interface IirDraftKeeper {
  /** The signed-in account's draft, or null. Never another account's. Throws when the store cannot be read. */
  load(upn: string): Promise<IirDraftRow | null>;
  /** Keep the draft, stamped with `upn`. Nobody signed in keeps nothing: an unstamped draft matches nobody. */
  save(upn: string, draft: unknown, label: string): Promise<void>;
  /** The caller's own draft, gone: sent to the queue, or cancelled by its owner. */
  clear(upn: string): Promise<void>;
}

export function indexedDbIirDraftStore(): IirDraftStore {
  const run = iirDatabase();
  return {
    get: async (upn) => (await run('readonly', (s) => s.get(upn) as IDBRequest<IirDraftRow | undefined>, DRAFTS)) ?? undefined,
    put: async (row) => void (await run('readwrite', (s) => s.put(row), DRAFTS)),
    delete: async (upn) => void (await run('readwrite', (s) => s.delete(upn), DRAFTS)),
  };
}

export function createIirDraftKeeper(store: IirDraftStore, now: () => number = () => Date.now()): IirDraftKeeper {
  return {
    async load(upn) {
      const me = normaliseUpn(upn);
      if (!me) return null;
      const row = await store.get(me);
      // The key IS the stamp, but the row is checked as well: a row that somehow carries another
      // stamp under this key is not this person's, and is not shown (fail closed).
      return row && rowUpn(row) === me ? row : null;
    },
    async save(upn, draft, label) {
      const me = normaliseUpn(upn);
      if (!me) return;
      await store.put({ upn: me, draft, label, savedAt: new Date(now()).toISOString() });
    },
    async clear(upn) {
      const me = normaliseUpn(upn);
      if (!me) return;
      await store.delete(me);
    },
  };
}

export function createIirQueue(overrides: Partial<IirQueueDeps> & { getUpn: () => string; getToken: () => Promise<string | null> }): IirQueue {
  const deps: IirQueueDeps = {
    idb: overrides.idb ?? indexedDbIirStore(),
    getUpn: overrides.getUpn,
    getToken: overrides.getToken,
    fetch: overrides.fetch ?? ((...a) => globalThis.fetch(...a)),
    now: overrides.now ?? (() => Date.now()),
    newId: overrides.newId ?? (() => crypto.randomUUID()),
    endpoint: overrides.endpoint ?? apiUrl('/receipt-inspections'),
    setTimer: overrides.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
    clearTimer: overrides.clearTimer ?? ((h) => clearTimeout(h as never)),
  };
  const listeners = new Set<(s: IirSnapshot) => void>();
  const unknown = (): IirSnapshot => ({ pending: null, held: [], refused: [], results: [...results], photosPending: null, photosFailed: [], photosHeld: 0 });
  const results: IirResult[] = [];
  let replaying = false;
  let backoff = BACKOFF_BASE_MS;
  let timer: unknown = null;

  const me = () => normaliseUpn(deps.getUpn());

  const split = (rows: IirRow[], photos: IirPhotoRow[], upn: string): IirSnapshot => {
    const mine = rows.filter((r) => mineToSend(r, upn));
    const myPhotos = photos.filter((p) => mineToSend(p, upn));
    return {
      photosPending: myPhotos.filter((p) => !p.failed).length,
      photosFailed: myPhotos.filter((p) => p.failed).map((p) => ({ photoId: p.id, context: p.context, message: p.failed!.message, reason: p.failed!.reason })),
      photosHeld: upn ? photos.length - myPhotos.length : 0,
      pending: mine.filter((r) => !r.refused).length,
      // Signed out: nothing is "held", everything is simply waiting (queue.ts).
      held: upn
        ? rows
            .filter((r) => !mineToSend(r, upn))
            .map((r) => ({ queueId: r.id, upn: rowUpn(r), context: r.context, capturedAtDevice: r.payload.capturedAtDevice }))
        : [],
      refused: mine
        .filter((r) => r.refused)
        .map((r) => ({ queueId: r.id, context: r.context, message: r.refused!.message, reason: r.refused!.reason })),
      results: [...results],
    };
  };

  async function snapshot(): Promise<IirSnapshot> {
    try {
      return split(await deps.idb.all(), await deps.idb.allPhotos(), me());
    } catch {
      return unknown();
    }
  }

  const publish = async () => {
    const s = await snapshot();
    for (const l of listeners) l(s);
    return s;
  };

  function scheduleReplay() {
    deps.clearTimer(timer);
    timer = deps.setTimer(() => void replay(), backoff);
    backoff = Math.min(BACKOFF_CAP_MS, backoff * 2);
  }

  async function enqueue(payload: Record<string, unknown>, context: IirContext, photos: readonly PhotoInput[] = []): Promise<IirRow> {
    const at = new Date(deps.now()).toISOString();
    // Spread first, the minted fields after: a caller cannot set the id or the time.
    const row: Omit<IirRow, 'id'> = {
      payload: { ...payload, clientInspectionId: deps.newId(), capturedAtDevice: at },
      context,
      enqueuedAt: at,
      refused: null,
      // ⚠ THE STAMP, from the session, last.
      upn: me(),
    };
    delete (row.payload as Record<string, unknown>).inspectedByUpn;
    const id = await deps.idb.add(row);
    // The photos after the inspection is safe: a photo that cannot be held loses only itself.
    for (const p of photos) {
      try {
        await deps.idb.addPhoto({
          upn: row.upn,
          clientInspectionId: row.payload.clientInspectionId,
          inspectionId: null,
          batchSequence: p.batchSequence,
          checkCode: p.checkCode,
          blob: p.blob,
          capturedAtDevice: p.capturedAtDevice,
          context,
          failed: null,
        });
      } catch {
        // Optional by ruling; the inspection is already held.
      }
    }
    await publish();
    return { ...row, id };
  }

  /** The photos of an inspection the server has just accepted learn its id. */
  async function photosAccepted(clientInspectionId: string, inspectionId: number) {
    for (const p of await deps.idb.allPhotos()) {
      if (p.clientInspectionId === clientInspectionId && p.inspectionId !== inspectionId) await deps.idb.putPhoto({ ...p, inspectionId });
    }
  }

  type PhotoStop = 'done' | 'network' | 'server_error' | 'auth' | 'store_unavailable';

  /** This account's photos whose inspection was accepted, one request each. Never touches an inspection. */
  async function sendPhotos(upn: string): Promise<{ sent: number; stop: PhotoStop }> {
    let sent = 0;
    let photos: IirPhotoRow[];
    try {
      photos = (await deps.idb.allPhotos()).filter((p) => !p.failed && p.inspectionId !== null && mineToSend(p, upn));
    } catch {
      return { sent, stop: 'store_unavailable' };
    }
    for (const p of photos) {
      let token: string | null;
      try {
        token = await deps.getToken();
      } catch {
        token = null;
      }
      if (!token) return { sent, stop: 'auth' };
      const q = new URLSearchParams({ capturedAtDevice: p.capturedAtDevice });
      if (p.batchSequence !== null) q.set('batch', String(p.batchSequence));
      if (p.checkCode) q.set('check', p.checkCode);
      let res: Response;
      try {
        res = await deps.fetch(`${deps.endpoint}/${p.inspectionId}/photos?${q}`, {
          method: 'POST',
          headers: { 'Content-Type': p.blob.type || 'image/jpeg', Authorization: `Bearer ${token}` },
          body: p.blob,
        });
      } catch {
        return { sent, stop: 'network' };
      }
      if (res.status === 401) return { sent, stop: 'auth' };
      // The server could not take it just now (Blob down, a restart): it waits, like the network.
      if (res.status >= 500) {
        await res.arrayBuffer().catch(() => undefined);
        return { sent, stop: 'server_error' };
      }
      const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
      try {
        if (res.ok) {
          await deps.idb.deletePhoto(p.id);
          sent += 1;
        } else {
          // Refused (4xx): kept, shown as "photo not sent", sent again or discarded only by its owner.
          await deps.idb.putPhoto({
            ...p,
            failed: {
              status: res.status,
              reason: typeof body?.reason === 'string' ? body.reason : null,
              message: typeof body?.error === 'string' ? body.error : `The server did not take the photo (${res.status}).`,
            },
          });
        }
      } catch {
        return { sent, stop: 'store_unavailable' };
      }
    }
    return { sent, stop: 'done' };
  }

  async function replay(): Promise<IirSnapshot & { sent: number; stoppedBecause: IirReplayStop }> {
    if (replaying) return { ...(await snapshot()), sent: 0, stoppedBecause: 'already_running' };
    replaying = true;
    let sent = 0;
    const stop = async (why: IirReplayStop) => ({ ...(await publish()), sent, stoppedBecause: why });
    try {
      for (;;) {
        let rows: IirRow[];
        try {
          rows = await deps.idb.all();
        } catch {
          return { ...unknown(), sent, stoppedBecause: 'store_unavailable' };
        }
        const upn = me();
        // Nothing (more) of this account's to send: now its photos, whose inspections are in.
        // They never change why the replay stopped - a photo is not an inspection.
        const afterInspections = async (why: IirReplayStop) => {
          const photos = upn ? await sendPhotos(upn) : null;
          // A photo still waiting on the network or a 5xx keeps growing the backoff (2 s .. 60 s);
          // only a clean pass resets it - else a Blob outage would be retried every 2 s.
          if (photos && (photos.stop === 'network' || photos.stop === 'server_error')) scheduleReplay();
          else if (why === 'drained') backoff = BACKOFF_BASE_MS;
          return stop(why);
        };
        if (!rows.some((r) => !r.refused)) return afterInspections('drained');
        if (!upn) return stop('nobody_signed_in');
        const row = rows.find((r) => !r.refused && mineToSend(r, upn));
        if (!row) return afterInspections('nothing_for_this_account');

        let token: string | null;
        try {
          token = await deps.getToken();
        } catch {
          token = null;
        }
        if (!token) return stop('auth');

        let res: Response;
        try {
          res = await deps.fetch(deps.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(row.payload),
          });
        } catch {
          scheduleReplay();
          return stop('network');
        }
        if (res.status >= 500) {
          scheduleReplay();
          return stop('server_error');
        }
        const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
        // A session problem keeps the row and stops. But a 403 the GATE wrote - no RECEIVE grant -
        // is a refusal like any other: shown with the server's sentence, kept as refused, and
        // never a silent block on everything behind it or on Hand over (review round 1, #6).
        if (res.status === 401 || (res.status === 403 && body?.reason !== 'no_current_grant')) return stop('auth');
        if (res.ok) {
          const failure = body?.failure as { line?: string } | null | undefined;
          results.unshift({
            clientInspectionId: row.payload.clientInspectionId,
            inspectionId: typeof body?.inspectionId === 'number' ? body.inspectionId : null,
            result: body?.result === 'PASS' || body?.result === 'FAIL' ? body.result : null,
            failureLine: typeof failure?.line === 'string' ? failure.line : null,
            context: row.context,
            at: deps.now(),
          });
          results.splice(MAX_RESULTS);
          try {
            const accepted = typeof body?.inspectionId === 'number' ? body.inspectionId : null;
            if (accepted !== null) await photosAccepted(row.payload.clientInspectionId, accepted);
            await deps.idb.delete(row.id);
          } catch {
            return stop('store_unavailable');
          }
        } else {
          // Refused, and the server did NOT store it: kept here, never retried.
          const refused = {
            status: res.status,
            reason: typeof body?.reason === 'string' ? body.reason : null,
            message: typeof body?.error === 'string' ? body.error : `The server refused this (${res.status}).`,
          };
          try {
            await deps.idb.put({ ...row, refused });
          } catch {
            return stop('store_unavailable');
          }
        }
        sent += 1;
        backoff = BACKOFF_BASE_MS;
      }
    } finally {
      replaying = false;
    }
  }

  async function retry(queueId: number, ownerUpn: string) {
    const row = (await deps.idb.all()).find((r) => r.id === queueId);
    if (!row || rowUpn(row) !== normaliseUpn(ownerUpn)) return;
    await deps.idb.put({ ...row, refused: null });
    await replay();
  }

  async function discard(queueId: number, ownerUpn: string) {
    const row = (await deps.idb.all()).find((r) => r.id === queueId);
    if (!row) return;
    if (rowUpn(row) !== normaliseUpn(ownerUpn)) {
      throw new Error(`Refusing to discard inspection ${queueId}: it belongs to ${rowUpn(row) || 'an account this device never recorded'}.`);
    }
    await deps.idb.delete(queueId);
    // Its photos go with it: they belong to an inspection the server never took.
    for (const p of await deps.idb.allPhotos()) {
      if (p.clientInspectionId === row.payload.clientInspectionId && p.inspectionId === null) await deps.idb.deletePhoto(p.id);
    }
    await publish();
  }

  async function ownPhoto(photoId: number, ownerUpn: string) {
    const p = (await deps.idb.allPhotos()).find((x) => x.id === photoId);
    return p && rowUpn(p) === normaliseUpn(ownerUpn) ? p : null;
  }

  async function retryPhoto(photoId: number, ownerUpn: string) {
    const p = await ownPhoto(photoId, ownerUpn);
    if (!p) return;
    await deps.idb.putPhoto({ ...p, failed: null });
    await replay();
  }

  async function discardPhoto(photoId: number, ownerUpn: string) {
    const p = await ownPhoto(photoId, ownerUpn);
    if (!p) throw new Error(`Refusing to discard photo ${photoId}: it is not yours.`);
    await deps.idb.deletePhoto(photoId);
    await publish();
  }

  return {
    enqueue,
    replay,
    snapshot,
    discard,
    retry,
    retryPhoto,
    discardPhoto,
    acknowledgeResults() {
      results.splice(0);
      void publish();
    },
    subscribe(l) {
      listeners.add(l);
      return () => void listeners.delete(l);
    },
    scheduleReplay,
  };
}
